import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../App';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { refresh, switchWorkspace } = useWorkspace();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Geçersiz doğrulama linki. Token bulunamadı.');
      return;
    }
    verifyEmail(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.get(`${API}/verify-email`, { params: { token } });
      const data = response.data || {};

      if (data.already_verified) {
        setStatus('already_verified');
        setMessage(data.message || 'Email adresiniz zaten doğrulanmış.');
        return;
      }

      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
        await checkAuth();
        await refresh();
        toast.success('E-posta doğrulandı!');

        if (data.redirectTo === '/dashboard' && data.workspaceId) {
          switchWorkspace(data.workspaceId);
          return;
        }
        navigate(data.redirectTo || '/dashboard', { replace: true });
        return;
      }

      setStatus('success');
      setMessage(data.message || 'Email adresiniz başarıyla doğrulandı!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Email doğrulama başarısız oldu.');
    }
  };

  const handleResend = async () => {
    const email = window.prompt('Doğrulama mailini hangi e-posta adresine göndereyim?');
    if (!email) return;
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success('Doğrulama maili gönderildi (eğer hesap mevcutsa).');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Mail gönderilemedi';
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <div className="text-center py-8">
          <Loader2 size={36} className="mx-auto mb-4 animate-spin" style={{ color: 'var(--accent)' }} />
          <div className="text-base font-semibold">Email Doğrulanıyor...</div>
          <div className="text-sm ox-text-muted mt-1">Lütfen bekleyin, email adresiniz doğrulanıyor.</div>
        </div>
      );
    }
    if (status === 'success' || status === 'already_verified') {
      const isAlready = status === 'already_verified';
      return (
        <div className="text-center py-8">
          <div
            className="mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              width: 56,
              height: 56,
              background: isAlready ? 'var(--accent-weak)' : 'var(--good-weak)',
              color: isAlready ? 'var(--accent)' : 'var(--good)',
            }}
          >
            <CheckCircle2 size={28} />
          </div>
          <div className="text-base font-semibold">
            {isAlready ? 'Zaten Doğrulanmış' : 'Email Doğrulandı'}
          </div>
          <div className="text-sm ox-text-muted mt-1 mb-5">{message}</div>
          <button className="ox-btn ox-btn-primary w-full" onClick={() => navigate('/')}>
            Giriş Sayfasına Git
          </button>
        </div>
      );
    }
    return (
      <div className="text-center py-8">
        <div
          className="mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{
            width: 56,
            height: 56,
            background: 'var(--danger-weak)',
            color: 'var(--danger)',
          }}
        >
          <XCircle size={28} />
        </div>
        <div className="text-base font-semibold">Doğrulama Başarısız</div>
        <div className="text-sm ox-text-muted mt-1 mb-5">{message}</div>
        <button
          className="ox-btn ox-btn-primary w-full"
          onClick={handleResend}
          disabled={resending}
        >
          {resending ? 'Gönderiliyor…' : 'Yeni Doğrulama Maili İste'}
        </button>
        <button className="ox-btn w-full mt-2" onClick={() => navigate('/')}>
          Giriş Sayfasına Git
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--surface-muted)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold tracking-tight ox-accent-text" style={{ letterSpacing: '-0.02em' }}>
            ONXFLOW
          </h1>
        </div>
        <div className="ox-surface rounded-lg p-6">
          {renderBody()}
        </div>
        <div className="text-center mt-6 text-[11.5px] ox-text-dim">
          © 2026 ONXFLOW. Tüm hakları saklıdır.
        </div>
      </div>
    </div>
  );
}
